import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { meta } from "@/lib/statusMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ScanLine, CheckCircle2, XCircle, Link2, ArrowRight, UserPlus, Search, Loader2, AlertTriangle, Camera, CameraOff } from "lucide-react";

const STATIONS = [
  { key: "intake", label: "Intake", status: "received" },
  { key: "dev", label: "Dev Tank", status: "processing" },
  { key: "scan", label: "Noritsu Scan", status: "scanning" },
  { key: "print", label: "Fuji Print", status: "printing" },
  { key: "dispatch", label: "Dispatch", status: "shipped" },
];

const EMPTY_FORM = { name: "", email: "", phone: "" };

const extractToken = (raw) => {
  let t = (raw || "").trim();
  if (!t) return "";
  const m = t.match(/[?&]token=([^&]+)/);
  if (m) return decodeURIComponent(m[1]);
  if (/^https?:\/\//i.test(t)) {
    const parts = t.split("/").filter(Boolean);
    return parts[parts.length - 1];
  }
  return t;
};

export default function ScanStation() {
  const [station, setStation] = useState("intake");
  const [value, setValue] = useState("");
  const [log, setLog] = useState([]);
  const [flash, setFlash] = useState(null); // {ok, text}
  const [unlinked, setUnlinked] = useState(null);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraErr, setCameraErr] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const inputRef = useRef();
  const scannerRef = useRef(null);
  const processRef = useRef(null);
  const lastScanRef = useRef({ text: "", t: 0 });
  const navigate = useNavigate();

  const focus = () => { if (!unlinked && !cameraOn) inputRef.current?.focus(); };
  useEffect(() => { focus(); }, [station]);

  // Duplicate guard: warn if the typed email/phone already matches a customer.
  useEffect(() => {
    if (!unlinked) return;
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!email && phone.replace(/\D/g, "").length < 7) { setDupMatch(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/customers/lookup", { params: { email, phone } });
        setDupMatch(r.data[0] || null);
      } catch { setDupMatch(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.email, form.phone, unlinked]);

  const beep = (ok = true) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = ok ? 880 : 200;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (ok ? 0.18 : 0.3));
      o.start();
      o.stop(ctx.currentTime + (ok ? 0.2 : 0.32));
      o.onended = () => { try { ctx.close(); } catch {} };
    } catch {}
  };
  const signalSuccess = () => { beep(true); setPulseKey((k) => k + 1); };
  const signalError = () => { beep(false); };

  const processToken = async (raw) => {
    const token = extractToken(raw);
    if (!token) return;
    setValue("");
    try {
      const res = await api.post("/scan", { token, station });
      if (res.data.type === "unlinked_label") {
        setUnlinked(res.data.label);
        setForm(EMPTY_FORM);
        setDupMatch(null);
        setCustQuery("");
        setCustResults([]);
        const o = await api.get("/orders");
        setOrders(o.data.filter((x) => !x.intake_label));
        return;
      }
      const ord = res.data.order;
      const ok = res.data.changed;
      setFlash({ ok: true, text: ok ? `${ord.order_number} → ${meta(res.data.status).label}` : `${ord.order_number} already ${meta(ord.status).label}` });
      setLog((l) => [{ id: Date.now(), order: ord.order_number, status: ord.status, at: new Date().toLocaleTimeString() }, ...l].slice(0, 12));
      signalSuccess();
      if (ok) toast.success(`${ord.order_number} advanced`);
    } catch (err) {
      setFlash({ ok: false, text: err?.response?.data?.detail || "Scan failed" });
      signalError();
      toast.error(err?.response?.data?.detail || "Scan failed");
    }
    setTimeout(() => setFlash(null), 2200);
    focus();
  };
  processRef.current = processToken;

  const submit = (e) => {
    e?.preventDefault();
    if (!value.trim()) return;
    processToken(value);
  };

  // Camera lifecycle
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      setCameraErr(null);
      try {
        const h = new Html5Qrcode("qr-reader", { verbose: false });
        scannerRef.current = h;
        await h.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decoded) => {
            const now = Date.now();
            if (decoded === lastScanRef.current.text && now - lastScanRef.current.t < 2500) return;
            lastScanRef.current = { text: decoded, t: now };
            processRef.current?.(decoded);
          },
          () => {}
        );
        if (cancelled) { try { await h.stop(); h.clear(); } catch {} }
      } catch (e) {
        setCameraErr(typeof e === "string" ? e : (e?.message || "Could not start camera. Check permissions."));
        setCameraOn(false);
        scannerRef.current = null;
      }
    };
    const stop = async () => {
      const h = scannerRef.current;
      scannerRef.current = null;
      if (h) { try { if (h.isScanning) await h.stop(); } catch {} try { h.clear(); } catch {} }
    };
    if (cameraOn) start(); else stop();
    return () => { cancelled = true; stop(); };
  }, [cameraOn]);

  // Pause scanning while the intake dialog is open to avoid re-reads.
  useEffect(() => {
    const h = scannerRef.current;
    if (!h) return;
    try {
      if (unlinked) { if (h.isScanning) h.pause(true); }
      else { h.resume(); }
    } catch {}
  }, [unlinked]);

  const finishIntake = (res, msg) => {
    const ord = res.data.order;
    toast.success(msg);
    setFlash({ ok: true, text: `${ord.order_number} intake · ${unlinked.code}` });
    signalSuccess();
    setLog((l) => [{ id: Date.now(), order: ord.order_number, status: "received", at: new Date().toLocaleTimeString() }, ...l].slice(0, 12));
    setUnlinked(null);
    setTimeout(() => setFlash(null), 2200);
    focus();
  };

  const linkLabel = async (orderId) => {
    try {
      await api.post(`/labels/${unlinked.token}/link`, { order_id: orderId });
      toast.success(`Label ${unlinked.code} linked`);
      setUnlinked(null);
    } catch { toast.error("Link failed"); }
    focus();
  };

  const intakeNew = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const res = await api.post(`/labels/${unlinked.token}/intake`, form);
      finishIntake(res, `New customer intake created for ${form.name}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Intake failed"); }
    finally { setBusy(false); }
  };

  const searchCustomers = async (q) => {
    setCustQuery(q);
    if (!q.trim()) { setCustResults([]); return; }
    try { const r = await api.get("/customers", { params: { q } }); setCustResults(r.data); }
    catch { /* ignore */ }
  };

  const intakeReturning = async (customer) => {
    setBusy(true);
    try {
      const res = await api.post(`/labels/${unlinked.token}/intake`, { customer_id: customer.id });
      finishIntake(res, `Intake created for ${customer.name}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Intake failed"); }
    finally { setBusy(false); }
  };

  const active = STATIONS.find((s) => s.key === station);

  return (
    <Layout>
      <div className="mb-6">
        <p className="mono text-xs uppercase tracking-[0.2em] text-brand mb-2">iPad Scan Station</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Scan & Advance</h1>
        <p className="text-muted-foreground text-sm mt-2">Select your station, then scan an envelope QR with the device camera or a USB/Bluetooth scanner. Each scan advances the order and emails the customer.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {STATIONS.map((s) => (
          <button key={s.key} onClick={() => setStation(s.key)} data-testid={`station-${s.key}`}
            className={`rounded-xl p-4 border text-left transition-all min-h-[76px] ${
              station === s.key ? "border-brand bg-brand/10" : "border-border bg-card hover:border-brand/40"
            }`}>
            <div className="mono text-[10px] uppercase tracking-wider" style={{ color: meta(s.status).color }}>{meta(s.status).label}</div>
            <div className="font-heading font-semibold mt-1">{s.label}</div>
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 flex flex-col items-center text-center relative overflow-hidden">
        {pulseKey > 0 && <div key={pulseKey} className="flash-green-overlay absolute inset-0 pointer-events-none z-20" style={{ background: "hsl(var(--primary))" }} data-testid="scan-success-flash" />}
        {cameraOn ? (
          <div className="w-full max-w-md">
            <div id="qr-reader" data-testid="qr-reader" className="w-full overflow-hidden rounded-2xl border-2 border-brand [&_video]:rounded-2xl" />
            <p className="mono text-xs text-muted-foreground mt-3 uppercase tracking-wider">
              Point the camera at an envelope QR · {active.label}
            </p>
            {cameraErr && <p className="text-ruby text-sm mt-2" data-testid="camera-error">{cameraErr}</p>}
            {flash && (
              <p className="mt-3 font-heading text-lg font-semibold" style={{ color: flash.ok ? meta(active.status).color : undefined }} data-testid="scan-flash">
                {flash.text}
              </p>
            )}
          </div>
        ) : (
          <div className={`w-full max-w-md rounded-2xl border-2 border-dashed p-10 transition-colors ${
            flash ? (flash.ok ? "border-status-scanning" : "border-ruby") : "border-border"
          } ${flash ? "animate-scan-pulse" : ""}`}>
            {flash ? (
              flash.ok
                ? <CheckCircle2 className="w-16 h-16 mx-auto text-status-scanning" style={{ color: meta(active.status).color }} />
                : <XCircle className="w-16 h-16 mx-auto text-ruby" />
            ) : <ScanLine className="w-16 h-16 mx-auto text-brand" />}
            <p className="mt-4 font-heading text-xl font-semibold" data-testid="scan-flash">
              {flash ? flash.text : `Scan for ${active.label}`}
            </p>
            <p className="mono text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {flash ? "" : "Awaiting QR code…"}
            </p>
          </div>
        )}

        <Button onClick={() => setCameraOn((v) => !v)} variant="outline" className="mt-5 h-11" data-testid="camera-toggle-btn">
          {cameraOn ? <><CameraOff className="w-4 h-4 mr-2" /> Stop camera</> : <><Camera className="w-4 h-4 mr-2" /> Scan with camera</>}
        </Button>

        <form onSubmit={submit} className="w-full max-w-md mt-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">or enter / wedge-scan manually</div>
          <input ref={inputRef} value={value} onChange={(e) => setValue(e.target.value)} onBlur={focus}
            data-testid="scan-input" placeholder="Scan or type QR value, press Enter"
            className="w-full bg-secondary rounded-xl px-4 py-4 text-center mono text-lg tracking-wide outline-none focus:ring-2 focus:ring-brand" />
          <Button type="submit" className="w-full mt-3 h-12 bg-brand hover:bg-brand-hover text-primary-foreground text-base" data-testid="scan-submit">
            Confirm scan
          </Button>
        </form>
      </div>

      {log.length > 0 && (
        <div className="mt-8">
          <h3 className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Recent scans</h3>
          <div className="space-y-2" data-testid="scan-log">
            {log.map((l) => (
              <div key={l.id} className="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-2.5 text-sm">
                <span className="mono font-semibold">{l.order}</span>
                <span style={{ color: meta(l.status).color }}>{meta(l.status).label}</span>
                <span className="mono text-xs text-muted-foreground">{l.at}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!unlinked} onOpenChange={(v) => !v && setUnlinked(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Link2 className="w-5 h-5 text-brand" /> New QR {unlinked?.code}
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1">This envelope QR isn't linked yet. Take in a walk-in customer, or link it to an existing order.</DialogDescription>

          <Tabs defaultValue="new" className="mt-2">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="new" data-testid="tab-new-customer"><UserPlus className="w-4 h-4 mr-1.5" />New</TabsTrigger>
              <TabsTrigger value="returning" data-testid="tab-returning-customer"><Search className="w-4 h-4 mr-1.5" />Returning</TabsTrigger>
              <TabsTrigger value="order" data-testid="tab-existing-order"><Link2 className="w-4 h-4 mr-1.5" />Order</TabsTrigger>
            </TabsList>

            <TabsContent value="new" className="space-y-3 mt-4">
              <div>
                <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="mt-1.5" data-testid="intake-name-input" />
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" className="mt-1.5" data-testid="intake-email-input" />
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-wider text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(336) 555-0123" className="mt-1.5" data-testid="intake-phone-input" />
              </div>
              {dupMatch && (
                <div className="rounded-md border border-status-processing bg-status-processing/10 px-3 py-2.5 text-sm flex items-start gap-2" data-testid="intake-dup-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#F59E0B" }} />
                  <div className="flex-1">
                    <p><span className="font-semibold">{dupMatch.name}</span> already exists{dupMatch.visit_count > 0 ? ` · ${dupMatch.visit_count} ${dupMatch.visit_count === 1 ? "visit" : "visits"}` : ""} <span className="mono text-xs opacity-70">({dupMatch.email}{dupMatch.phone ? ` · ${dupMatch.phone}` : ""})</span>.</p>
                    <button onClick={() => intakeReturning(dupMatch)} disabled={busy} className="mono text-xs underline mt-1 hover:text-brand" data-testid="intake-dup-use-btn">Use this customer instead →</button>
                  </div>
                </div>
              )}
              <Button onClick={intakeNew} disabled={busy} className="w-full bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="intake-new-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <UserPlus className="w-4 h-4 mr-1.5" />}Create intake &amp; link QR
              </Button>
            </TabsContent>

            <TabsContent value="returning" className="mt-4">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={custQuery} onChange={(e) => searchCustomers(e.target.value)} placeholder="Search name, email or phone" className="pl-9" data-testid="intake-customer-search" />
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2 mt-3" data-testid="intake-customer-results">
                {custResults.map((c) => (
                  <button key={c.id} onClick={() => intakeReturning(c)} disabled={busy} data-testid={`intake-customer-${c.id}`}
                    className="w-full flex items-center justify-between bg-secondary hover:bg-brand hover:text-primary-foreground rounded-lg px-4 py-3 text-left transition-colors group">
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        {c.name}
                        {c.visit_count > 0 && <span className="mono text-[10px] font-normal opacity-70">· {c.visit_count} {c.visit_count === 1 ? "visit" : "visits"}</span>}
                      </div>
                      <div className="text-xs opacity-70 mono">{c.email}{c.phone ? ` · ${c.phone}` : ""}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
                {custQuery.trim() && custResults.length === 0 && (
                  <p className="text-sm text-muted-foreground py-6 text-center">No match. Add them under the <span className="font-semibold">New</span> tab.</p>
                )}
                {!custQuery.trim() && (
                  <p className="text-sm text-muted-foreground py-6 text-center">Start typing to find a returning customer.</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="order" className="mt-4">
              <div className="max-h-72 overflow-y-auto space-y-2" data-testid="link-order-list">
                {orders.map((o) => (
                  <button key={o.id} onClick={() => linkLabel(o.id)} data-testid={`link-order-${o.order_number.replace("#", "")}`}
                    className="w-full flex items-center justify-between bg-secondary hover:bg-brand hover:text-primary-foreground rounded-lg px-4 py-3 text-left transition-colors group">
                    <div><div className="mono font-semibold">{o.order_number}</div><div className="text-xs opacity-70">{o.customer_name}</div></div>
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
                {orders.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No unlinked orders available.</p>}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
